import { Flex } from '@chakra-ui/layout';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import { maskitoTransform } from '@maskito/core';
import {
  CellContext,
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useCallback } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { CommitmentMechanicsFormData, LenderTableData } from '../types';
import {
  DeleteIconButton,
  IconButtonV2,
} from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  USD_MASK_OPTIONS,
  TextInput as BaseTextInput,
} from 'packages/common-frontend/components/InputsBETA/TextInput';
import { Typography } from 'packages/common-frontend/components/Typography';
import { TableBETA } from 'packages/common-frontend/tables/TableBETA';
import { remToPx } from 'packages/common-frontend/theme/utils';
import { AddLenderModal } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/LenderTable/AddLenderModal';
import {
  useIsOverTotalCommitment,
  useLenderDiffResult,
  useTotalProRataAmount,
  useValue,
} from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/LenderTable/hooks';
import { formatNumber } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/LenderTable/utils';
import { useConfirmDeleteContext } from '~/core/routes/settings/implementations/common/ConfirmDeleteModalContext';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import {
  DiffTag,
  PlaceholderInput,
  ShowWhileEditing,
  TextInput,
} from '~/core/routes/settings/implementations/common/DiffInputs';

// Define the meta type for tanstack table
// The tanstack docs show a different way to type this property, but it is global
// to all tables. So we will just do it this way instead. See: https://tanstack.com/table/v8/docs/api/core/table#meta
type LenderTableMeta = {
  remove: (index: number) => void;
};

export const TotalProRataAmount = () => {
  const total = useTotalProRataAmount();
  const form = useFormContext<CommitmentMechanicsFormData>();
  const isEditing = useCreditAgreementStore((store) => store.isEditing);

  const onClick = useCallback(() => {
    form.setValue('wipForm.totalCommitment', total);
  }, [total, form]);

  return (
    <TextInput<CommitmentMechanicsFormData>
      name="wipForm.totalCommitment"
      diffName="diffForm.totalCommitment"
      placeholder="$0.00"
      mask="usd"
      required
      isDisabled={!isEditing}
      inputRightElement={
        <ShowWhileEditing>
          <IconButtonV2
            icon={<Icon icon="calculator" />}
            aria-label="Select to replace Total Commitment with the sum of lender pro rata $ amounts."
            variant="v2-ghost"
            onClick={onClick}
            size="sm"
          />
        </ShowWhileEditing>
      }
    />
  );
};

export const TotalProRataPercentage = () => {
  const total = useTotalProRataAmount();
  const totalCommitment = useValue(`wipForm.totalCommitment`);
  const totalPercentage = total / (totalCommitment ?? 0);

  return <span>{formatNumber(totalPercentage, 'percent')}</span>;
};

export const ProRataAmountCell = ({
  row,
}: CellContext<LenderTableData, any>) => {
  const diff = useLenderDiffResult(row.original.lenderId);
  const isEditing = useCreditAgreementStore((store) => store.isEditing);

  if (!isEditing) {
    const value =
      diff.currentLender?.proRataAmount ?? diff.previousLender?.proRataAmount;

    return (
      <PlaceholderInput
        value={maskitoTransform(String(value ?? ''), USD_MASK_OPTIONS)}
        tag={<DiffTag diff={diff.diffResult} />}
      />
    );
  }

  return (
    <BaseTextInput<CommitmentMechanicsFormData>
      name={`wipForm.lenders.${row.index}.proRataAmount`}
      placeholder="$0.00"
      mask="usd"
      isDisabled={!isEditing}
      required
    />
  );
};

export const ProRataPercentageCell = ({
  row,
}: CellContext<LenderTableData, any>) => {
  const proRataAmount = useValue(`wipForm.lenders.${row.index}.proRataAmount`);
  const totalCommitment = useValue(`wipForm.totalCommitment`);
  const percentage = (proRataAmount ?? 0) / (totalCommitment ?? 0);

  const diff = useLenderDiffResult(row.original.lenderId);
  const isEditing = useCreditAgreementStore((store) => store.isEditing);

  if (!isEditing && diff.diffResult?.status === 'deleted') {
    return <span>-</span>;
  }

  return <span>{formatNumber(percentage, 'percent')}</span>;
};

const RemoveLenderCell = ({
  row,
  table,
}: CellContext<LenderTableData, any>) => {
  const name = row.original.lenderName;
  const { showConfirmDeleteModal } = useConfirmDeleteContext();

  // Access the meta property with proper typing
  const meta = table.options.meta as LenderTableMeta;

  const onClick = () => {
    showConfirmDeleteModal(name, () => meta.remove(row.index));
  };

  return (
    <ShowWhileEditing>
      <Flex justify="right">
        <DeleteIconButton label="Remove Lender" onClick={onClick} />
      </Flex>
    </ShowWhileEditing>
  );
};

export const OverAmountAlert = () => {
  const isOver = useIsOverTotalCommitment();
  if (!isOver) return null;

  return (
    <Alert status="error">
      <AlertIcon />
      <Flex direction="column">
        <AlertTitle>
          The sum of the pro rata $ amounts exceeds the total commitment. Review
          and make adjustments.
        </AlertTitle>
      </Flex>
    </Alert>
  );
};

const columnDefs: ColumnDef<LenderTableData, any>[] = [
  {
    accessorKey: 'lenderName',
    header: 'Lender',
    enableSorting: false,
    footer: 'Total Commitment',
  },
  {
    accessorKey: 'proRataPercentage',
    enableSorting: false,
    header: 'Pro-Rata %',
    cell: ProRataPercentageCell,
    footer: TotalProRataPercentage,
  },
  {
    accessorKey: 'proRataAmount',
    enableSorting: false,
    header: 'Pro-Rata $',
    cell: ProRataAmountCell,
    footer: TotalProRataAmount,
  },
  {
    id: 'removeLender',
    header: '',
    size: remToPx(1),
    enableSorting: false,
    cell: RemoveLenderCell,
  },
];

export const LenderTable = ({
  formKey,
}: {
  formKey: 'wipForm' | 'diffForm';
}) => {
  const { append, remove, fields } = useFieldArray<
    CommitmentMechanicsFormData,
    'wipForm.lenders' | 'diffForm.lenders'
  >({
    name: `${formKey}.lenders`,
  });

  const meta: LenderTableMeta = { remove };

  const table = useReactTable({
    data: fields,
    columns: columnDefs,
    meta,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.lenderId,
  });

  const disclosure = useDisclosure();

  return (
    <Flex direction="column" gap="1rem">
      <Flex justifyContent="space-between" direction="column" gap="1rem">
        <Flex alignItems="center" justifyContent="space-between">
          <Typography variant="section-title" trim>
            Total Commitment
          </Typography>
          <ShowWhileEditing>
            <Button
              colorScheme="v2-aurora"
              variant="secondary"
              leftIcon={<Icon icon="plus" />}
              onClick={disclosure.onOpen}
            >
              Add Lender
            </Button>
          </ShowWhileEditing>
        </Flex>
        <Typography variant="base-muted">
          Add lenders and assign their pro rata share to calculate the total
          commitment amount. At least one lender is required. The total
          commitment amount can also be edited directly.
        </Typography>
        <AddLenderModal
          disclosure={disclosure}
          onAddLender={(lender) => append({ proRataAmount: 0, ...lender })}
        />
      </Flex>
      {fields.length > 0 && <TableBETA table={table} />}
      <OverAmountAlert />
    </Flex>
  );
};
